import React, { useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Search } from "lucide-react";

interface MicrositeGallerySectionProps {
  images: string[];
  themeColor?: string;
}

const MicrositeGallerySection: React.FC<MicrositeGallerySectionProps> = ({
  images,
  themeColor = "#885ABB",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const prevImage = useCallback(
    () => setCurrentIndex((i) => (i === 0 ? images.length - 1 : i - 1)),
    [images]
  );

  const nextImage = useCallback(
    () => setCurrentIndex((i) => (i === images.length - 1 ? 0 : i + 1)),
    [images]
  );

  if (!images.length) return null;

  return (
    <section id="gallery" className="py-16 bg-white">
      {/* Header */}
      <div className="text-center mb-10">
        <h2
          className="text-3xl font-semibold tracking-wide font-serif"
        >
          Our Gallery.
        </h2>

        <p className="text-gray-500 mt-2 max-w-2xl mx-auto">
          Discover the creativity and precision behind our finest work. Each
          piece tells a unique story.
        </p>
      </div>

      {/* Image Grid */}
      <div className="px-10">
           <div className="flex justify-center items-center flex-wrap gap-6 px-6">
        {images.map((img, index) => (
          <div
            key={index}
            onClick={() => openModal(index)}
            className="relative group cursor-pointer overflow-hidden rounded-md transition-all duration-300"
            style={{ border: `4px solid ${themeColor}` }}
          >
            <img
              src={img}
              alt={`Gallery ${index}`}
              className="object-cover transition-all duration-500 group-hover:opacity-60 group-hover:scale-105 h-60 w-60"
            />

            {/* Hover Overlay */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
              <div
                className="p-2 rounded-md bg-white bg-opacity-80 flex items-center justify-center"
                style={{ border: `2px solid ${themeColor}` }}
              >
                <Search
                  size={20}
                  style={{ color: themeColor }}
                  className="transition-transform duration-300 group-hover:scale-110"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      </div>
    
      {/* Modal Lightbox */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={closeModal} // clicking outside closes modal
        >
          <div
            className="relative flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()} // stop closing when clicking on the image area
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-5 right-5 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700 transition"
            >
              <X size={22} />
            </button>

            {/* Left Arrow */}
            <button
              onClick={prevImage}
              className="absolute left-5 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700 transition"
            >
              <ChevronLeft size={26} />
            </button>

            {/* Image */}
            <div className="max-w-[80vw] max-h-[80vh] flex justify-center items-center">
              <img
                src={images[currentIndex]}
                alt="Preview"
                className="object-contain rounded-lg shadow-lg w-[800px] h-[600px]"
              />
            </div>

            {/* Right Arrow */}
            <button
              onClick={nextImage}
              className="absolute right-5 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700 transition"
            >
              <ChevronRight size={26} />
            </button>

            {/* Counter */}
            <div className="absolute bottom-6 text-gray-300 text-sm">
              {currentIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MicrositeGallerySection;
